import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Spinner,
  Button,
  Input,
  Chip,
  Avatar,
  Tabs,
  Tab,
} from '@nextui-org/react';
import {
  MessageSquare,
  Search,
  Pin,
  Lock,
  ArrowUp,
  ArrowDown,
  MessageCircle,
  Eye,
  Users,
  Flame,
  Clock,
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  PenSquare,
  X,
  CornerDownRight,
} from 'lucide-react';
import forumService, {
  ForumCategory,
  ForumTopic,
  ForumPost,
  ForumSearchResult,
} from '../services/forumService';
import { useAuth } from '../contexts/AuthContext';
import CreateTopicModal from '../components/forum/CreateTopicModal';
import LoginPromptModal from '../components/forum/LoginPromptModal';

const TEAL = '#3FCED2';
const PAGE_SIZE = 20;

type SortMode = 'hot' | 'newest' | 'top' | 'active';

const ForumPage: React.FC = () => {
  const navigate = useNavigate();
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const { isAuthenticated } = useAuth();

  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [topics, setTopics] = useState<ForumTopic[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<ForumCategory | null>(null);
  const [sortBy, setSortBy] = useState<SortMode>('hot');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');

  // Server-Suche (/api/forum/search)
  const [searchResults, setSearchResults] = useState<ForumSearchResult | null>(null);
  const [activeSearchQuery, setActiveSearchQuery] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchUnavailable, setSearchUnavailable] = useState(false);

  // Topic-Erstellung
  const [createOpen, setCreateOpen] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);

  // ---------- Load categories ----------
  useEffect(() => {
    (async () => {
      try {
        setLoadingCategories(true);
        const cats = await forumService.getCategories();
        setCategories(cats);
        if (categorySlug) {
          const found = findCategoryBySlug(cats, categorySlug);
          if (found) setSelectedCategory(found);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Forum-Kategorien:', err);
        setError('Forum konnte nicht geladen werden');
      } finally {
        setLoadingCategories(false);
      }
    })();
  }, [categorySlug]);

  // ---------- Load topics ----------
  const loadTopics = useCallback(async (opts: { reset?: boolean } = {}) => {
    const reset = opts.reset ?? true;
    if (reset) {
      setLoadingTopics(true);
      setPage(1);
    } else {
      setLoadingMore(true);
    }
    try {
      const nextPage = reset ? 1 : page + 1;
      const result = await forumService.getTopics({
        categoryId: selectedCategory?.id || selectedCategory?._id,
        sort: sortBy,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      setTopics((prev) => (reset ? result.topics : [...prev, ...result.topics]));
      setPage(result.page ?? nextPage);
      setHasMore(result.hasMore ?? false);
    } catch (err) {
      console.error('Fehler beim Laden der Topics:', err);
    } finally {
      setLoadingTopics(false);
      setLoadingMore(false);
    }
  }, [page, selectedCategory, sortBy]);

  useEffect(() => {
    loadTopics({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, sortBy]);

  // ---------- Search (client-side filter on loaded topics) ----------
  const filteredTopics = useMemo(() => {
    if (!searchTerm.trim()) return topics;
    const lower = searchTerm.toLowerCase();
    return topics.filter((t) => {
      const title = (t.title || '').toLowerCase();
      const author = (t.authorName || '').toLowerCase();
      const content = (t.contentPlain || '').toLowerCase();
      return title.includes(lower) || author.includes(lower) || content.includes(lower);
    });
  }, [topics, searchTerm]);

  // ---------- Server-Suche via /api/forum/search ----------
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults(null);
    setActiveSearchQuery(null);
    setSearchUnavailable(false);
  }, []);

  const runSearch = useCallback(async () => {
    const query = searchTerm.trim();
    if (query.length < 2) {
      setSearchResults(null);
      setActiveSearchQuery(null);
      setSearchUnavailable(false);
      return;
    }
    setSearching(true);
    setActiveSearchQuery(query);
    const result = await forumService.search(query, {
      type: 'all',
      categoryId: selectedCategory?.id || selectedCategory?._id,
    });
    setSearching(false);
    if (result) {
      setSearchResults(result);
      setSearchUnavailable(false);
    } else {
      // Server-Suche nicht verfügbar → Fallback auf lokale Filterung
      setSearchResults(null);
      setSearchUnavailable(true);
    }
  }, [searchTerm, selectedCategory]);

  /** Slug eines Topics anhand der Topic-ID auflösen (für Post-Suchtreffer). */
  const slugForTopicId = useCallback(
    (tid?: string | null): string | null => {
      if (!tid) return null;
      const pool = [...(searchResults?.topics || []), ...topics];
      const found = pool.find((t) => (t.id || t._id) === tid);
      return found?.slug || null;
    },
    [searchResults, topics],
  );

  // ---------- Topic-Erstellung ----------
  const handleNewTopicClick = useCallback(() => {
    if (!isAuthenticated) {
      setLoginPromptOpen(true);
      return;
    }
    setCreateOpen(true);
  }, [isAuthenticated]);

  if (loadingCategories) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Forum | DartCloud</title>
        </Helmet>
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" style={{ color: TEAL }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Forum | DartCloud</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <AlertTriangle size={64} className="mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>Forum | DartCloud</title>
        <meta
          name="description"
          content="Das DartCloud Forum - Diskutiere mit der Dart-Community über alles rund um Darts."
        />
        <link rel="canonical" href="https://dartcloudapp.de/forum" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1">
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <MessageSquare size={40} style={{ color: TEAL }} />
              Forum
            </h1>
            <p className="text-gray-400">
              Diskutiere mit der Dart-Community
            </p>
          </div>
          <Button
            startContent={<PenSquare size={18} />}
            style={{ backgroundColor: TEAL, color: '#000' }}
            className="font-semibold flex-shrink-0"
            onPress={handleNewTopicClick}
          >
            Neues Thema
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar: Categories */}
          <aside className="lg:col-span-3 order-2 lg:order-1">
            <Card className="bg-zinc-900 border-zinc-800 lg:sticky lg:top-24">
              <CardBody className="p-0">
                <div className="px-4 py-3 border-b border-zinc-800">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400">
                    Kategorien
                  </h3>
                </div>
                <ul className="divide-y divide-zinc-800 max-h-[60vh] overflow-y-auto">
                  <li>
                    <CategoryButton
                      isSelected={selectedCategory === null}
                      onClick={() => {
                        setSelectedCategory(null);
                        navigate('/forum');
                      }}
                      name="Alle Kategorien"
                      icon="🏠"
                    />
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.id || cat._id}>
                      <CategoryButton
                        isSelected={(cat.id || cat._id) === (selectedCategory?.id || selectedCategory?._id)}
                        onClick={() => {
                          setSelectedCategory(cat);
                          if (cat.slug) navigate(`/forum/${cat.slug}`);
                        }}
                        name={cat.name || 'Kategorie'}
                        icon={cat.icon}
                        color={cat.color}
                        topicCount={cat.topicCount}
                      />
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </aside>

          {/* Main: Topics list */}
          <main className="lg:col-span-9 order-1 lg:order-2">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Input
                placeholder="Forum durchsuchen (Enter für Suche)..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (e.target.value.trim().length === 0) {
                    setSearchResults(null);
                    setActiveSearchQuery(null);
                    setSearchUnavailable(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runSearch();
                  }
                }}
                startContent={<Search size={18} className="text-gray-400" />}
                endContent={
                  searchTerm.length > 0 ? (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="text-gray-400 hover:text-white"
                      aria-label="Suche zurücksetzen"
                    >
                      <X size={16} />
                    </button>
                  ) : undefined
                }
                className="flex-1"
                classNames={{
                  input: 'bg-zinc-900',
                  inputWrapper: 'bg-zinc-900 border-zinc-800',
                }}
              />
              <Button
                onPress={runSearch}
                isLoading={searching}
                variant="flat"
                className="bg-zinc-800 text-white"
                startContent={!searching ? <Search size={16} /> : undefined}
              >
                Suchen
              </Button>
            </div>

            {searchUnavailable && activeSearchQuery && (
              <div className="flex items-center gap-2 rounded-xl px-4 py-2 mb-4 text-xs text-gray-400 bg-zinc-900 border border-zinc-800">
                <AlertTriangle size={14} className="text-yellow-500" />
                Die Server-Suche ist derzeit nicht verfügbar – es werden lokale
                Treffer aus den geladenen Topics angezeigt.
              </div>
            )}

            {/* Sort tabs */}
            <Tabs
              selectedKey={sortBy}
              onSelectionChange={(k) => setSortBy(k as SortMode)}
              variant="solid"
              classNames={{
                tabList: 'bg-zinc-900 border border-zinc-800',
                cursor: '',
                tab: 'data-[selected=true]:text-black',
                tabContent: 'group-data-[selected=true]:text-black',
              }}
              className="mb-4"
            >
              <Tab
                key="hot"
                title={
                  <div className="flex items-center gap-1">
                    <Flame size={16} />
                    Hot
                  </div>
                }
              />
              <Tab
                key="newest"
                title={
                  <div className="flex items-center gap-1">
                    <Clock size={16} />
                    Neueste
                  </div>
                }
              />
              <Tab
                key="top"
                title={
                  <div className="flex items-center gap-1">
                    <TrendingUp size={16} />
                    Top
                  </div>
                }
              />
              <Tab
                key="active"
                title={
                  <div className="flex items-center gap-1">
                    <MessageCircle size={16} />
                    Aktiv
                  </div>
                }
              />
            </Tabs>

            {/* Selected Category Header */}
            {selectedCategory && (
              <Card className="bg-zinc-900 border-zinc-800 mb-4">
                <CardBody className="p-4 flex flex-row items-center gap-3">
                  {selectedCategory.icon && (
                    <span className="text-3xl">{selectedCategory.icon}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold">{selectedCategory.name}</h2>
                    {selectedCategory.description && (
                      <p className="text-sm text-gray-400 line-clamp-2">
                        {selectedCategory.description}
                      </p>
                    )}
                  </div>
                  {selectedCategory.isArchived && (
                    <Chip size="sm" color="warning" variant="flat">
                      Archiviert
                    </Chip>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Server-Suchergebnisse */}
            {searchResults && activeSearchQuery ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold">
                    Suchergebnisse für „{activeSearchQuery}“
                  </h2>
                  <Button
                    size="sm"
                    variant="flat"
                    className="bg-zinc-800 text-gray-300"
                    startContent={<X size={14} />}
                    onPress={clearSearch}
                  >
                    Suche zurücksetzen
                  </Button>
                </div>

                {searchResults.topics.length === 0 &&
                searchResults.posts.length === 0 ? (
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardBody className="p-12 text-center">
                      <Search size={48} className="mx-auto text-gray-600 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-400 mb-1">
                        Keine Treffer
                      </h3>
                      <p className="text-gray-500 text-sm">
                        Versuche einen anderen Suchbegriff
                      </p>
                    </CardBody>
                  </Card>
                ) : (
                  <>
                    {searchResults.topics.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-2">
                          Topics ({searchResults.topics.length})
                        </h3>
                        <div className="space-y-3">
                          {searchResults.topics.map((topic) => (
                            <TopicCard
                              key={topic.id || topic._id}
                              topic={topic}
                              onOpen={() => {
                                if (topic.slug) navigate(`/forum/topic/${topic.slug}`);
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {searchResults.posts.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-2">
                          Beiträge ({searchResults.posts.length})
                        </h3>
                        <div className="space-y-3">
                          {searchResults.posts.map((post) => (
                            <PostSearchCard
                              key={post.id || post._id}
                              post={post}
                              topicSlug={slugForTopicId(post.topicId)}
                              onOpen={(s) => navigate(`/forum/topic/${s}`)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : loadingTopics ? (
              <div className="flex justify-center items-center h-64">
                <Spinner size="lg" style={{ color: TEAL }} />
              </div>
            ) : filteredTopics.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardBody className="p-12 text-center">
                  <MessageSquare size={64} className="mx-auto text-gray-600 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-400 mb-1">
                    Keine Topics gefunden
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {searchTerm
                      ? 'Versuche einen anderen Suchbegriff'
                      : 'In dieser Kategorie wurden noch keine Topics erstellt.'}
                  </p>
                </CardBody>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredTopics.map((topic) => (
                  <TopicCard
                    key={topic.id || topic._id}
                    topic={topic}
                    onOpen={() => {
                      if (topic.slug) navigate(`/forum/topic/${topic.slug}`);
                    }}
                  />
                ))}

                {hasMore && (
                  <div className="flex justify-center pt-4">
                    <Button
                      onPress={() => loadTopics({ reset: false })}
                      isLoading={loadingMore}
                      isDisabled={loadingMore}
                      variant="flat"
                      className="bg-zinc-800 text-white"
                    >
                      {loadingMore ? 'Lädt...' : 'Mehr laden'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Modals */}
      <CreateTopicModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        categories={categories}
        selectedCategory={selectedCategory}
        onCreated={(topic) => {
          if (topic?.slug) {
            navigate(`/forum/topic/${topic.slug}`);
          } else {
            loadTopics({ reset: true });
          }
        }}
      />
      <LoginPromptModal
        isOpen={loginPromptOpen}
        onClose={() => setLoginPromptOpen(false)}
        message="Bitte melde dich an, um ein neues Thema zu erstellen."
      />
    </div>
  );
};

// ============= Post Search Result Card =============
interface PostSearchCardProps {
  post: ForumPost;
  topicSlug: string | null;
  onOpen: (slug: string) => void;
}

const PostSearchCard: React.FC<PostSearchCardProps> = ({
  post,
  topicSlug,
  onOpen,
}) => {
  const created = forumService.formatRelativeTime(post.createdAt);
  const excerpt = forumService.extractPlainText(
    post.contentPlain || post.content || '',
  );
  const clickable = !!topicSlug;

  return (
    <Card
      className={`bg-zinc-900 border-zinc-800 transition-colors ${
        clickable ? 'hover:bg-zinc-900/80 cursor-pointer' : ''
      }`}
      isPressable={clickable}
      onPress={() => {
        if (topicSlug) onOpen(topicSlug);
      }}
    >
      <CardBody className="p-4">
        <div className="flex items-center gap-2 mb-1 text-xs text-gray-500">
          <CornerDownRight size={12} className="text-gray-600" />
          <Avatar
            size="sm"
            src={post.authorAvatarUrl || undefined}
            name={post.authorName || '?'}
            className="w-5 h-5 text-[10px]"
          />
          <span className="text-gray-300">{post.authorName || 'Anonym'}</span>
          {created && <span>· {created}</span>}
          {post.topicTitle && (
            <span className="truncate text-gray-500">
              in „{post.topicTitle}“
            </span>
          )}
        </div>
        <p className="text-sm text-gray-300 line-clamp-3">{excerpt}</p>
      </CardBody>
    </Card>
  );
};

// ============= Category Button =============
interface CategoryButtonProps {
  isSelected: boolean;
  onClick: () => void;
  name: string;
  icon?: string | null;
  color?: string | null;
  topicCount?: number;
}

const CategoryButton: React.FC<CategoryButtonProps> = ({
  isSelected,
  onClick,
  name,
  icon,
  color,
  topicCount,
}) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-4 py-3 transition-colors ${
      isSelected ? 'bg-zinc-800/80' : 'hover:bg-zinc-800/40'
    }`}
  >
    <div className="flex items-center gap-3">
      <div
        className="flex-shrink-0 w-1 h-8 rounded-full"
        style={{ backgroundColor: isSelected ? color || TEAL : 'transparent' }}
      />
      {icon && <span className="text-lg flex-shrink-0">{icon}</span>}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium line-clamp-1 ${
            isSelected ? 'text-white' : 'text-gray-300'
          }`}
        >
          {name}
        </p>
        {typeof topicCount === 'number' && (
          <p className="text-xs text-gray-500">{topicCount} Topics</p>
        )}
      </div>
      <ChevronRight size={14} className="text-gray-600 flex-shrink-0" />
    </div>
  </button>
);

// ============= Topic Card =============
interface TopicCardProps {
  topic: ForumTopic;
  onOpen: () => void;
}

const TopicCard: React.FC<TopicCardProps> = ({ topic, onOpen }) => {
  const created = forumService.formatRelativeTime(topic.createdAt);
  const lastPost = forumService.formatRelativeTime(topic.lastPostTime);
  // Inhalt ist teils Quill-Delta-JSON; contentPlain ist nicht immer gesetzt
  const preview = forumService.extractPlainText(
    topic.contentPlain && topic.contentPlain.trim() ? topic.contentPlain : topic.content,
  );

  return (
    <Card
      className="bg-zinc-900 border-zinc-800 hover:bg-zinc-900/80 transition-colors cursor-pointer"
      isPressable
      onPress={onOpen}
    >
      <CardBody className="p-4">
        <div className="flex gap-4">
          {/* Vote count */}
          <div className="flex flex-col items-center justify-start pt-1 flex-shrink-0 w-12">
            <ArrowUp size={16} className="text-gray-500" />
            <span className="text-sm font-bold text-white">{topic.score ?? 0}</span>
            <ArrowDown size={16} className="text-gray-500" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {topic.isPinned && (
                <Pin size={14} style={{ color: TEAL }} />
              )}
              {topic.isLocked && <Lock size={14} className="text-gray-500" />}
              {topic.categoryName && (
                <Chip
                  size="sm"
                  variant="flat"
                  className="bg-zinc-800 text-gray-400 text-[10px] h-5"
                >
                  {topic.categoryName}
                </Chip>
              )}
            </div>

            <h3 className="text-base md:text-lg font-semibold text-white line-clamp-2 mb-1">
              {topic.title || 'Topic'}
            </h3>

            {preview && (
              <p className="text-sm text-gray-400 line-clamp-2 mb-2">
                {preview}
              </p>
            )}

            {topic.tags && topic.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {topic.tags.slice(0, 3).map((tag, i) => (
                  <Chip
                    key={i}
                    size="sm"
                    variant="bordered"
                    className="border-zinc-700 text-gray-400 text-[10px] h-5"
                  >
                    #{tag}
                  </Chip>
                ))}
              </div>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Avatar
                  size="sm"
                  src={topic.authorAvatarUrl || undefined}
                  name={topic.authorName || '?'}
                  className="w-5 h-5 text-[10px]"
                />
                <span>{topic.authorName || 'Anonym'}</span>
              </div>
              <span>· {created}</span>
              <span className="flex items-center gap-1">
                <MessageCircle size={12} /> {topic.postCount ?? 0}
              </span>
              <span className="flex items-center gap-1">
                <Eye size={12} /> {topic.viewCount ?? 0}
              </span>
              {(topic.uniqueParticipants ?? 0) > 1 && (
                <span className="flex items-center gap-1">
                  <Users size={12} /> {topic.uniqueParticipants}
                </span>
              )}
              {lastPost && (
                <span className="ml-auto text-gray-400">
                  Letzte Aktivität: {lastPost}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

// ============= Helpers =============
function findCategoryBySlug(
  categories: ForumCategory[],
  slug: string,
): ForumCategory | null {
  for (const c of categories) {
    if (c.slug === slug) return c;
    if (c.subcategories && c.subcategories.length > 0) {
      const found = findCategoryBySlug(c.subcategories, slug);
      if (found) return found;
    }
  }
  return null;
}

export default ForumPage;
